// Static sub-discipline mappings for each discipline
export const disciplineSubDisciplines = {
  // Mathematics
  "mathematics": [
    {
      id: "foundational-fields",
      name: "Foundational Fields",
      description: "The core pillars upon which all other branches of mathematics are constructed",
      slug: "foundational-fields",
      subtopics: [
        "Arithmetic - Natural numbers and operations",
        "Algebra - Variables, equations, abstract structures", 
        "Geometry - Space, form, and transformation",
        "Trigonometry - Angular relationships and waves",
        "Calculus - Limits, derivatives, and integrals",
        "Linear Algebra - Vectors and matrices",
        "Set Theory & Logic - Mathematical foundations",
        "Abstract Algebra - Groups, rings, and fields"
      ]
    },
    {
      id: "applied-mathematics",
      name: "Applied Mathematics",
      description: "Mathematical tools for modeling real-world phenomena and solving practical problems",
      slug: "applied-mathematics",
      subtopics: [
        "Statistics & Probability - Data analysis and uncertainty",
        "Numerical Methods - Computational algorithms",
        "Optimization - Decision theory and efficiency",
        "Mathematical Physics - Quantum and classical mechanics",
        "Differential Equations - Dynamic system modeling",
        "Computational Mathematics - Algorithms and data structures"
      ]
    },
    {
      id: "historical-development",
      name: "Mathematics Across Time",
      description: "The historical development of mathematical thought across cultures and eras",
      slug: "historical-development",
      subtopics: [
        "Ancient Mathematics - Babylonian, Egyptian, Greek",
        "Medieval & Islamic Contributions",
        "Renaissance & Enlightenment Advances",
        "19th Century Formalism & Foundations",
        "Rise of Modern Mathematics",
        "Galois, Hilbert, Gödel, Turing"
      ]
    },
    {
      id: "interdisciplinary-pathways",
      name: "Interdisciplinary Mathematics",
      description: "Mathematics' integration with philosophy, physics, engineering, and beyond",
      slug: "interdisciplinary-pathways",
      subtopics: [
        "Mathematics & Philosophy - Logic and truth",
        "Mathematics & Physics - Symmetry and spacetime",
        "Mathematics & Computer Science - Algorithms and complexity",
        "Mathematics & Engineering - Structural analysis",
        "Mathematics & Economics - Game theory and modeling",
        "Mathematics & Biology - Population dynamics"
      ]
    }
  ],

  // Health Sciences
  "health-sciences": [
    {
      id: "core-medical-sciences",
      name: "Core Medical Sciences",
      description: "The biological and physiological foundation of modern healthcare practice",
      slug: "core-medical-sciences",
      subtopics: [
        "Anatomy and Physiology",
        "Biochemistry and Molecular Biology",
        "Pathology and Microbiology",
        "Pharmacology",
        "Internal Medicine",
        "Surgery and Emergency Medicine",
        "Pediatrics and Geriatrics",
        "Medical Diagnostics"
      ]
    },
    {
      id: "allied-health",
      name: "Allied Health & Clinical Practice",
      description: "Specialized disciplines that support and extend primary medical care",
      slug: "allied-health",
      subtopics: [
        "Nursing Science",
        "Physiotherapy and Occupational Therapy",
        "Rehabilitation Medicine",
        "Pain Management",
        "Medical Imaging and Radiologic Sciences",
        "Laboratory Medicine",
        "Biomedical Technology",
        "Clinical Biochemistry"
      ]
    },
    {
      id: "mental-behavioral-health",
      name: "Mental & Behavioral Health",
      description: "The psychological, neurological, and behavioral dimensions of human health",
      slug: "mental-behavioral-health",
      subtopics: [
        "Mental Health Disorders",
        "Cognitive-Behavioral Therapy",
        "Psychometric Assessments",
        "Trauma-Informed Care",
        "Psychiatry and Psychopharmacology",
        "Neuropsychology",
        "Brain Imaging and Cognition",
        "Behavioral Therapy"
      ]
    },
    {
      id: "public-global-health",
      name: "Public & Global Health",
      description: "Population-level health, preventive strategies, and the global burden of disease",
      slug: "public-global-health",
      subtopics: [
        "Epidemiology and Biostatistics",
        "Health Policy and Management",
        "Global Health Organizations",
        "Disease Prevention",
        "Environmental and Occupational Health",
        "Nutrition and Preventive Medicine",
        "Climate Change and Health",
        "One Health Approaches"
      ]
    }
  ],

  // Natural Sciences
  "natural-sciences": [
    {
      id: "physical-sciences",
      name: "Physical Sciences",
      description: "Understanding matter, energy, and the fundamental forces of nature",
      slug: "physical-sciences",
      subtopics: [
        "Physics - Classical and quantum mechanics",
        "Chemistry - Molecular interactions and reactions",
        "Astronomy - Celestial bodies and cosmology",
        "Materials Science - Properties and applications"
      ]
    },
    {
      id: "life-sciences",
      name: "Life Sciences",
      description: "The study of living organisms and biological processes",
      slug: "life-sciences",
      subtopics: [
        "Biology - Cell and molecular biology",
        "Ecology - Environmental systems",
        "Genetics - Heredity and evolution",
        "Biotechnology - Applied biological sciences"
      ]
    },
    {
      id: "interdisciplinary",
      name: "Interdisciplinary Sciences",
      description: "Emerging fields that bridge traditional scientific boundaries",
      slug: "interdisciplinary",
      subtopics: [
        "Biophysics - Physics principles in biology",
        "Environmental Science - Ecological systems",
        "Science Policy - Science in society",
        "Philosophy of Science - Scientific methodology"
      ]
    }
  ],

  // Engineering
  "engineering": [
    {
      id: "core-disciplines",
      name: "Core Engineering Disciplines",
      description: "Traditional engineering fields focused on design and problem-solving",
      slug: "core-disciplines",
      subtopics: [
        "Civil Engineering - Infrastructure and construction",
        "Mechanical Engineering - Machines and systems",
        "Electrical Engineering - Power and electronics",
        "Chemical Engineering - Process design"
      ]
    },
    {
      id: "technology-applications",
      name: "Technology & Applications",
      description: "Modern engineering focused on emerging technologies",
      slug: "technology-applications",
      subtopics: [
        "Computer Engineering - Hardware and software",
        "Biomedical Engineering - Medical devices",
        "Environmental Engineering - Sustainable systems",
        "Aerospace Engineering - Flight and space"
      ]
    },
    {
      id: "innovation-future",
      name: "Innovation & Future",
      description: "Cutting-edge engineering addressing future challenges",
      slug: "innovation-future",
      subtopics: [
        "AI and Machine Learning - Intelligent systems",
        "Renewable Energy - Sustainable power",
        "Nanotechnology - Molecular engineering",
        "Robotics - Automated systems"
      ]
    }
  ],

  // History (independent discipline)
  "history": [
    {
      id: "historical-periods",
      name: "Historical Periods",
      description: "Major eras and epochs in human civilization",
      slug: "historical-periods",
      subtopics: [
        "Ancient History - Early civilizations",
        "Medieval Period - Middle Ages",
        "Modern Era - Renaissance to present",
        "Contemporary History - 20th-21st centuries"
      ]
    },
    {
      id: "regional-history",
      name: "Regional History",
      description: "History of different geographical regions and civilizations",
      slug: "regional-history",
      subtopics: [
        "European History",
        "Asian History",
        "African History",
        "American History"
      ]
    },
    {
      id: "thematic-history",
      name: "Thematic History",
      description: "Cross-temporal themes in historical development",
      slug: "thematic-history",
      subtopics: [
        "Social History",
        "Cultural History",
        "Economic History",
        "Intellectual History"
      ]
    },
    {
      id: "historiography-methodology",
      name: "Historiography & Methodology",
      description: "The study of historical writing and research methods",
      slug: "historiography-methodology",
      subtopics: [
        "Historical Methods",
        "Source Criticism",
        "Historiographical Schools",
        "Digital History"
      ]
    }
  ],

  // Law (independent discipline)
  "law": [
    {
      id: "foundational-legal-traditions",
      name: "Foundational Legal Traditions",
      description: "Major legal systems and their development",
      slug: "foundational-legal-traditions",
      subtopics: [
        "Common Law - Anglo-American tradition",
        "Civil Law - Continental European system",
        "Religious Law - Islamic, Jewish, Canon law",
        "Indigenous Legal Systems"
      ]
    },
    {
      id: "legal-theory-jurisprudence",
      name: "Legal Theory & Jurisprudence",
      description: "Philosophical foundations of law and justice",
      slug: "legal-theory-jurisprudence",
      subtopics: [
        "Natural Law Theory",
        "Legal Positivism",
        "Critical Legal Studies",
        "Feminist Jurisprudence"
      ]
    },
    {
      id: "legal-history",
      name: "Legal History",
      description: "Historical development of legal systems and institutions",
      slug: "legal-history",
      subtopics: [
        "Ancient Legal Systems",
        "Medieval Law",
        "Modern Legal Development",
        "Constitutional History"
      ]
    },
    {
      id: "major-branches-law",
      name: "Major Branches of Law",
      description: "Core areas of legal practice and study",
      slug: "major-branches-law",
      subtopics: [
        "Constitutional Law",
        "Criminal Law",
        "Civil Law",
        "Administrative Law"
      ]
    },
    {
      id: "specialized-legal-fields",
      name: "Specialized Legal Fields",
      description: "Specialized areas of legal practice",
      slug: "specialized-legal-fields",
      subtopics: [
        "International Law",
        "Environmental Law",
        "Corporate Law",
        "Human Rights Law"
      ]
    }
  ]
};

export const getSubDisciplinesForDiscipline = (disciplineSlug: string) => {
  return disciplineSubDisciplines[disciplineSlug as keyof typeof disciplineSubDisciplines] || [];
};

export const getSubDisciplineBySlug = (disciplineSlug: string, subDisciplineSlug: string) => {
  const subDisciplines = getSubDisciplinesForDiscipline(disciplineSlug);
  return subDisciplines.find(sub => sub.slug === subDisciplineSlug);
}; 